<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from 'vue'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { useInactivityTracker } from '@/composables/useInactivityTracker'

const { showWarning, dismissWarning } = useInactivityTracker()

// Countdown timer
const secondsRemaining = ref(300) // 5 minutes in seconds
let countdownInterval: number | null = null

// Format remaining time as MM:SS
const formattedTime = computed(() => {
  const minutes = Math.floor(secondsRemaining.value / 60)
  const seconds = secondsRemaining.value % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
})

// Start countdown when warning is shown
watch(
  () => showWarning.value,
  (isShowing: boolean) => {
    if (isShowing) {
      // Reset countdown to 5 minutes
      secondsRemaining.value = 300

      // Start countdown
      countdownInterval = window.setInterval(() => {
        if (secondsRemaining.value > 0) {
          secondsRemaining.value--
        } else {
          // Clear interval when countdown reaches zero
          if (countdownInterval) {
            clearInterval(countdownInterval)
            countdownInterval = null
          }
        }
      }, 1000)
    } else {
      // Clear interval when warning is dismissed
      if (countdownInterval) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }
    }
  },
)

// Clean up interval on component unmount
onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})
</script>

<template>
  <AlertDialog :open="showWarning" @update:open="showWarning = $event">
    <AlertDialogContent>
      <AlertDialogHeader>
        <div class="flex flex-col items-center gap-4">
          <div
            class="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center"
          >
            <i-heroicons-clock class="w-6 h-6 text-yellow-600" />
          </div>
          <AlertDialogTitle class="text-center"
            >Session Timeout Warning</AlertDialogTitle
          >
          <AlertDialogDescription class="text-center">
            Due to inactivity, you will be automatically logged out in
            <span class="font-semibold">{{ formattedTime }}</span
            >.
            <br />
            Would you like to continue your session?
          </AlertDialogDescription>
        </div>
      </AlertDialogHeader>
      <AlertDialogFooter class="flex justify-center">
        <AlertDialogAction @click="dismissWarning">
          Continue Session
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
